/**
 * Permission helpers for the access-control API shape:
 *
 * permission_detail: [
 *   { feature: "Call List", action: { can_upload_calls: true, ... } },
 *   ...
 * ]
 *
 * Feature names and action keys must match the API exactly.
 */

/** Feature names as returned by permission_details API */
export const FEATURES = {
  ACCESS_LOGS: "Access Logs",
  ARCHIVED_CALLS: "Archived Calls",
  CALL_LIST: "Call List",
  CONTACT: "Contact",
  HOME_PAGE: "Home Page",
  MY_THINGS: "My Things",
  OPPORTUNITY: "Opportunity",
  /** Backend key remains "Promise to Pay"; UI label is "Call Status". */
  PROMISE_TO_PAY: "Promise to Pay",
  QUALITY_AUDIT: "Quality Audit",
  REPORTS: "Reports",
  SERVICE_REQUEST: "Service Request",
  USER_MANAGEMENT: "User Management",
  /** Present in older nav code; not in current SuperAdmin payload */
  DASHBOARD: "Dashboard",
};

/**
 * UI-only display names for features.
 * Backend API still uses FEATURES values (e.g. "Promise to Pay").
 */
export const FEATURE_DISPLAY_NAMES = {
  [FEATURES.PROMISE_TO_PAY]: "Call Status",
};

/**
 * UI-only display names for action keys.
 * Backend still uses the raw keys (e.g. can_view_promise_to_pay_page).
 */
export const ACTION_DISPLAY_NAMES = {
  can_view_promise_to_pay_page: "Can View Call Status Page",
};

/** Human-readable feature label for role/permission UI. */
export function getFeatureDisplayName(featureName) {
  if (!featureName) return "";
  return FEATURE_DISPLAY_NAMES[featureName] || featureName;
}

/**
 * Human-readable action label for role/permission UI.
 * Falls back to title-casing the underscore key.
 */
export function getActionDisplayName(actionKey) {
  if (!actionKey) return "";
  if (ACTION_DISPLAY_NAMES[actionKey]) return ACTION_DISPLAY_NAMES[actionKey];
  return actionKey
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Action keys grouped by feature (must match API).
 * Use these constants everywhere to avoid typos.
 */
export const ACTIONS = {
  [FEATURES.ACCESS_LOGS]: {
    VIEW: "can_view_access_logs",
  },
  [FEATURES.ARCHIVED_CALLS]: {
    ADD: "can_add_archived_calls",
    VIEW: "can_view_archived_calls",
  },
  [FEATURES.CALL_LIST]: {
    LIVE_CALL: "can_access_live_call",
    EDIT_TRANSCRIPTION: "can_edit_transcription",
    REVIEW_CALL: "can_review_call",
    UPLOAD: "can_upload_calls",
    VIEW_DEVIANCE: "can_view_deviance_calls",
    VIEW_PROCESSED: "can_view_processed_calls",
  },
  [FEATURES.CONTACT]: {
    CREATE: "can_create_contacts",
    VIEW: "can_view_contacts",
  },
  [FEATURES.HOME_PAGE]: {
    COLLECTIONS: "can_view_collections",
    STRATEGY_SCORES: "can_view_collection_strategy_scores",
    COLLECTION_TRENDS: "can_view_collection_trends",
    COMPLIANCE_QUALITY: "can_view_compilance_quality", // API spelling
    INTENT_ANALYSIS: "can_view_intent_analysis",
    RECENT_CALLS: "can_view_recent_calls",
    TREND_ANALYSIS: "can_view_trend_analysis",
  },
  [FEATURES.MY_THINGS]: {
    CREATE_TASKS: "can_create_tasks",
    VIEW_TASKS: "can_view_tasks",
  },
  [FEATURES.OPPORTUNITY]: {
    VIEW: "can_view_opportunity_section",
  },
  [FEATURES.PROMISE_TO_PAY]: {
    VIEW: "can_view_promise_to_pay_page",
  },
  [FEATURES.QUALITY_AUDIT]: {
    ADD_CAMPAIGN_SCRIPTS: "can_add_campaign_scripts",
    ADD_CHECKPOINTS: "can_add_checkpoints",
    ADD_QUESTIONNAIRE: "can_add_questionnaire",
    DELETE_CAMPAIGN_SCRIPTS: "can_delete_campaign_scripts",
    DELETE_CHECKPOINTS: "can_delete_checkpoints",
    DELETE_QUESTIONNAIRE: "can_delete_questionnaire",
    EDIT_CAMPAIGN_SCRIPTS: "can_edit_campaign_scripts",
    VIEW_CAMPAIGN_SCRIPTS: "can_view_campaign_scripts",
    VIEW_PRIMARY: "can_view_primary_consumable_insights",
    VIEW_QUALITY_AUDIT: "can_view_quality_audit",
    VIEW_QUESTIONNAIRE: "can_view_questionnaire",
    VIEW_SECONDARY: "can_view_secondary_consumable_insights",
  },
  [FEATURES.REPORTS]: {
    DOWNLOAD_COLLECTION: "can_download_collection_reports",
    EDIT_COLLECTION: "can_edit_collection_reports",
    EDIT_COLLECTION_STRATEGY: "can_edit_collection_strategy_reports",
    VIEW_COLLECTION_REPORTS: "can_view_collection_reports",
    VIEW_COLLECTION_STRATEGY: "can_view_collection_strategy_reports",
    VIEW_MY_REPORTS: "can_view_my_reports",
    VIEW_QUALITY_AUDIT_REPORTS: "can_view_quality_audit_reports",
    CREATE_MY_REPORTS: "create_my_reports",
    DELETE_MY_REPORTS: "delete_my_reports",
    EDIT_DEVIANCE: "edit_deviance_analysis_reports",
    VIEW_AGENT_SCORECARD: "view_agent_scorecard",
    VIEW_COLLECTION_REPORT: "view_collection_report",
    VIEW_CUSTOMER_INTENT: "view_customer_intent_report",
    VIEW_DEVIANCE: "view_deviance_analysis_reports",
    VIEW_PERFORMANCE: "view_performance_report",
    VIEW_PRODUCT_INTENT: "view_product_by_intent_report",
    VIEW_PTP: "view_promise_to_pay_report",
    VIEW_TRANSCRIPTION_AUDIT: "view_transcription_audit_reports",
  },
  [FEATURES.SERVICE_REQUEST]: {
    VIEW: "can_view_service_request",
  },
  [FEATURES.USER_MANAGEMENT]: {
    CREATE_USERS: "can_create_new_users",
    CREATE_ROLE: "can_create_role",
    DELETE_ROLE: "can_delete_role",
    DOWNLOAD_USERS: "can_download_users",
    VIEW_APP_FEATURE: "can_view_app_feature",
    VIEW_ROLE: "can_view_role",
    VIEW_USERS: "can_view_users",
  },
};

/**
 * @param {Array|{permission_detail?: Array}} source - permission_detail array or full API response
 * @returns {Array}
 */
export function normalizePermissionDetail(source) {
  if (!source) return [];
  if (Array.isArray(source)) return source;
  if (Array.isArray(source.permission_detail)) return source.permission_detail;
  return [];
}

/**
 * @param {Array} permissionDetail
 * @param {string} featureName
 */
export function getFeature(permissionDetail, featureName) {
  const list = normalizePermissionDetail(permissionDetail);
  return list.find((item) => item && item.feature === featureName) || null;
}

/**
 * True if the user has access to the feature.
 *
 * Feature presence alone is not enough: the API may still return the feature
 * with all actions set to false (e.g. Promise to Pay with
 * can_view_promise_to_pay_page: false). In that case access is denied.
 *
 * Access is granted when at least one action under the feature is true
 * (object map) or listed (array form).
 */
export function hasFeature(permissionDetail, featureName) {
  const feature = getFeature(permissionDetail, featureName);
  if (!feature || feature.action == null) return false;

  const actions = feature.action;

  if (Array.isArray(actions)) {
    // Non-empty grant list: ["can_view"] or [{ name: "can_view" }, ...]
    return actions.some((item) => {
      if (typeof item === "string") return item.length > 0;
      if (item && typeof item === "object") {
        return (
          Object.values(item).some((v) => v === true) ||
          !!(item.name || item.action || item.key)
        );
      }
      return false;
    });
  }

  if (typeof actions === "object") {
    // { can_view_promise_to_pay_page: false } → false
    // { can_view_promise_to_pay_page: true }  → true
    return Object.values(actions).some((v) => v === true);
  }

  return false;
}

/**
 * True if a specific action under a feature is granted.
 * Supports:
 *  - action as object map: { can_view: true }
 *  - action as string array: ["can_view"]
 *  - action as object array: [{ name: "can_view" }] / [{ action: "can_view" }]
 */
export function hasPermission(permissionDetail, featureName, actionName) {
  if (!featureName || !actionName) return false;
  const feature = getFeature(permissionDetail, featureName);
  if (!feature || feature.action == null) return false;

  const actions = feature.action;

  if (Array.isArray(actions)) {
    return actions.some((item) => {
      if (typeof item === "string") return item === actionName;
      if (item && typeof item === "object") {
        return (
          item[actionName] === true ||
          item.name === actionName ||
          item.action === actionName ||
          item.key === actionName
        );
      }
      return false;
    });
  }

  if (typeof actions === "object") {
    return !!actions[actionName];
  }

  return false;
}

/**
 * True if any of the listed actions is granted for the feature.
 */
export function hasAnyPermission(permissionDetail, featureName, actionNames) {
  if (!Array.isArray(actionNames) || actionNames.length === 0) return false;
  return actionNames.some((actionName) =>
    hasPermission(permissionDetail, featureName, actionName),
  );
}

/**
 * True if every listed action is granted for the feature.
 */
export function hasAllPermissions(permissionDetail, featureName, actionNames) {
  if (!Array.isArray(actionNames) || actionNames.length === 0) return false;
  return actionNames.every((actionName) =>
    hasPermission(permissionDetail, featureName, actionName),
  );
}

/**
 * Quality Audit accounts (formerly hard-coded MYABCMR01 / MYABCQT01).
 * Granted when user can view or edit collection reports.
 */
export function isQualityAuditAccount(permissionDetail) {
  const ra = ACTIONS[FEATURES.REPORTS];
  return hasAnyPermission(permissionDetail, FEATURES.REPORTS, [
    ra.VIEW_COLLECTION_REPORTS,
    ra.EDIT_COLLECTION,
  ]);
}

/**
 * Negotiation Strategy accounts (formerly hard-coded MYABCNG01 / GNLNNT01).
 * Granted when user can view or edit collection strategy reports.
 */
export function isNegotiationStrategyAccount(permissionDetail) {
  const ra = ACTIONS[FEATURES.REPORTS];
  return hasAnyPermission(permissionDetail, FEATURES.REPORTS, [
    ra.VIEW_COLLECTION_STRATEGY,
    ra.EDIT_COLLECTION_STRATEGY,
  ]);
}

/**
 * Default My Things landing path from API permission keys.
 * Driven entirely by permission_details (not username / role strings).
 *
 * Priority:
 * 1. can_view_collection_reports | can_edit_collection_reports
 *    → /my-things/my-thing  (quality audit accounts)
 * 2. can_view_collection_strategy_reports | can_edit_collection_strategy_reports
 *    → /my-things/collection-strategy  (negotiation strategy accounts)
 * 3. else → /my-things/task
 */
export function getMyThingsDefaultPath(permissionDetail) {
  if (isQualityAuditAccount(permissionDetail)) {
    return "/my-things/my-thing";
  }
  if (isNegotiationStrategyAccount(permissionDetail)) {
    return "/my-things/collection-strategy";
  }
  return "/my-things/task";
}

/**
 * Default User Management landing path from API permission keys.
 * view users → manage-user, else view role → manage-role, else app feature.
 */
export function getUserManagementDefaultPath(permissionDetail) {
  const um = ACTIONS[FEATURES.USER_MANAGEMENT];
  if (hasPermission(permissionDetail, FEATURES.USER_MANAGEMENT, um.VIEW_USERS)) {
    return "/user-management/manage-user";
  }
  if (hasPermission(permissionDetail, FEATURES.USER_MANAGEMENT, um.VIEW_ROLE)) {
    return "/user-management/manage-role";
  }
  if (
    hasPermission(permissionDetail, FEATURES.USER_MANAGEMENT, um.VIEW_APP_FEATURE)
  ) {
    return "/user-management/app-feature";
  }
  return "/user-management/manage-user";
}
